import { useState } from "react";
import { generatePassphrase, generatePassword } from "../../vault/generator";
import { estimateEntropyBits } from "../../vault/health";
import { useT } from "../../i18n";
import { Icons } from "../icons";

// Password / passphrase generator, opened from a secret field. Moved out of ItemEditor unchanged
// except for i18n and the strength read-out.
export function Generator({ onUse }: { onUse: (v: string) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"password" | "passphrase">("password");
  const [length, setLength] = useState(20);
  const [words, setWords] = useState(5);
  const [upper, setUpper] = useState(true);
  const [digits, setDigits] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [preview, setPreview] = useState("");

  function gen() {
    const v = mode === "password" ? generatePassword({ length, upper, digits, symbols }) : generatePassphrase(words);
    setPreview(v);
    return v;
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          gen();
        }}
        className="mt-1 text-xs text-accent hover:underline"
      >
        {t("Generate…")}
      </button>
    );
  }

  const bits = preview ? Math.round(estimateEntropyBits(preview)) : 0;
  return (
    <div className="mt-1 rounded border border-border bg-panel p-2 text-xs">
      <div className="mb-2 flex gap-2">
        {(["password", "passphrase"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded px-2 py-0.5 ${mode === m ? "bg-accent text-white" : "border border-border"}`}
          >
            {m === "password" ? t("password") : t("passphrase")}
          </button>
        ))}
      </div>
      {mode === "password" ? (
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1">
            {t("len")}
            <input type="range" min={8} max={64} value={length} onChange={(e) => setLength(+e.target.value)} />
            {length}
          </label>
          <Check label="A-Z" checked={upper} onChange={setUpper} />
          <Check label="0-9" checked={digits} onChange={setDigits} />
          <Check label="!@#" checked={symbols} onChange={setSymbols} />
        </div>
      ) : (
        <label className="flex items-center gap-1">
          {t("words")}
          <input type="range" min={3} max={10} value={words} onChange={(e) => setWords(+e.target.value)} />
          {words}
        </label>
      )}
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 break-all rounded bg-panel2 px-2 py-1 font-mono">{preview}</code>
        <button type="button" onClick={() => setPreview(gen())} aria-label={t("Regenerate")} className="rounded border border-border px-2 py-1 hover:border-accent">
          <Icons.refresh className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            onUse(preview);
            setOpen(false);
          }}
          className="rounded bg-accent px-2 py-1 text-white hover:bg-accent2"
        >
          {t("Use")}
        </button>
      </div>
      {preview && <div className="mt-1 text-[11px] text-muted">≈ {bits} {t("bits of entropy")}</div>}
    </div>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-1">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
